import type { audience } from '@bb/contracts';

export type FilterNode = audience.FilterNode;
export type GroupNode = Extract<FilterNode, { kind: 'and' | 'or' }>;
export type FilterLeaf = Extract<FilterNode, { kind: 'filter' }>;

let counter = 0;
/** Short unique node id for new groups and filters (ids only need to be unique within one definition). */
export const nodeId = (prefix: string) => `${prefix}${Date.now().toString(36)}${(++counter).toString(36)}`;

export const newFilter = (): FilterLeaf => ({ kind: 'filter', id: nodeId('f'), filter: { type: 'custom_attribute', key: '', dataType: 'string', operator: 'equals', value: '' } });
export const newGroup = (): GroupNode => ({ kind: 'and', id: nodeId('g'), children: [newFilter()] });

/** Copy of `node` with the node `id` replaced by `update(node)`. */
export function updateNode(node: FilterNode, id: string, update: (node: FilterNode) => FilterNode): FilterNode {
  if (node.id === id) return update(node);
  if (node.kind === 'filter') return node;
  if (node.kind === 'not') return { ...node, child: updateNode(node.child, id, update) };
  return { ...node, children: node.children.map((child) => updateNode(child, id, update)) };
}

/** Copy of a group without the child `id`. */
export function removeChild(group: GroupNode, id: string): GroupNode {
  return { ...group, children: group.children.filter((c) => c.id !== id) };
}

/** Copy of a group with the child at `index` moved by `delta` (keyboard reorder and drag share it). */
export function moveChild(group: GroupNode, index: number, delta: number): GroupNode {
  const target = index + delta;
  if (target < 0 || target >= group.children.length) return group;
  const children = [...group.children];
  const [moved] = children.splice(index, 1);
  children.splice(target, 0, moved!);
  return { ...group, children };
}
